/**
 * API Service Layer for St David University Lodge
 *
 * This file contains mock API functions for development.
 *
 * BACKEND DEVELOPERS: Replace mock implementations with real API calls.
 * Each function documents the expected endpoint and request/response format.
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = window.APP_CONFIG?.apiEndpoint || (BACKEND_URL ? `${BACKEND_URL}/api` : 'https://h3qoa48ig5.execute-api.eu-west-2.amazonaws.com/api');

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE,
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const storedUser = localStorage.getItem('sdul_user');
  if (storedUser) {
    const user = JSON.parse(storedUser);
    if (user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
  }
  return config;
});

// ============================================
// PUBLIC SUPPORT ENDPOINTS
// ============================================

/**
 * Submit public contact form
 * POST /api/support/public
 */
export const submitPublicSupport = async (data) => {
  return apiClient.post('/support/public', data);
};

/**
 * Submit member support form (authenticated)
 * POST /api/support/member
 */
export const submitMemberSupport = async (data) => {
  return apiClient.post('/support/member', data);
};

/**
 * Submit meeting minute comment (authenticated)
 * POST /api/minutes/{minuteId}/comments
 */
export const submitMinuteComment = async (minuteId, comment) => {
  return apiClient.post(`/minutes/${minuteId}/comments`, { comment });
};

// ============================================
// LEARNING MODULE ENDPOINTS
// ============================================

/**
 * Get learning catalogue
 */
export const getLearningCatalogue = async () => {
  return apiClient.get('/learning');
};

/**
 * Get specific learning unit
 */
export const getLearningUnit = async (unitId) => {
  return apiClient.get(`/learning/units/${unitId}`);
};

// ============================================
// COLLECTIONS ENDPOINTS
// ============================================

// Mock collections data as fallback
const MOCK_COLLECTIONS = [
  {
    id: 'col1',
    title: 'The Square and Compasses',
    description: 'A beautifully crafted set of ceremonial square and compasses, dating from the early 19th century. This piece was donated to the lodge by Brother William MacTavish in 1892. The gold inlay work on the compasses depicts traditional Scottish motifs.',
    images: [
      '/images/collections/square-and-compasses.png',
    ],
    has3DModel: false,
  },
  {
    id: 'col2',
    title: 'Historic Lodge Bible',
    description: 'The original lodge Bible used during ceremonies since 1756 when the lodge was renamed to St David. This King James Version bears the signatures of past Masters on its flyleaf.',
    images: [
      '/images/collections/lodge-bible.png',
    ],
    has3DModel: false,
  },
  {
    id: 'col3',
    title: "Master's Chair",
    description: "An ornate Master's chair commissioned in 1850, featuring carved oak with Masonic symbolism. The chair remains in use during installations to this day.",
    images: [
      '/images/collections/masters-chair.png',
    ],
    has3DModel: true,
  },
];

/**
 * Get all collections
 */
export const getCollections = async () => {
  try {
    const response = await apiClient.get('/collections');
    // If backend returns empty list, use mock data
    if (response.data.collections && response.data.collections.length > 0) {
      return response;
    }
    return { data: { collections: MOCK_COLLECTIONS } };
  } catch (error) {
    console.warn('Failed to fetch collections from API, falling back to mock data');
    return { data: { collections: MOCK_COLLECTIONS } };
  }
};

/**
 * Get collection by ID
 */
export const getCollection = async (id) => {
  try {
    return await apiClient.get(`/collections/${id}`);
  } catch (error) {
    console.warn(`Failed to fetch collection ${id} from API, falling back to mock data`);
    const collection = MOCK_COLLECTIONS.find(c => c.id === id);
    if (!collection) throw error;
    return { data: collection };
  }
};

// ============================================
// DEGREE WORK ENDPOINTS
// ============================================

/**
 * Get all degree work materials
 */
export const getDegreeWork = async () => {
  return apiClient.get('/degree-work');
};

// ============================================
// MEETING MINUTES ENDPOINTS
// ============================================

/**
 * Get all meeting minutes
 */
export const getMeetingMinutes = async () => {
  return apiClient.get('/minutes');
};

/**
 * Get meeting minute by ID
 */
export const getMeetingMinute = async (id) => {
  return apiClient.get(`/minutes/${id}`);
};

// ============================================
// ADMIN: USER MANAGEMENT ENDPOINTS
// ============================================

/**
 * Get all users (admin only)
 */
export const getUsers = async () => {
  return apiClient.get('/admin/users');
};

/**
 * Create single user (admin only)
 */
export const createUser = async (userData) => {
  return apiClient.post('/admin/users', userData);
};

/**
 * Bulk create users (admin only)
 */
export const bulkCreateUsers = async (users) => {
  return apiClient.post('/admin/users/bulk', { users });
};

/**
 * Disable user (admin only)
 */
export const disableUser = async (userId) => {
  return apiClient.put(`/admin/users/${userId}/disable`);
};

/**
 * Reset user password (admin only)
 */
export const resetUserPassword = async (userId, temporaryPassword) => {
  return apiClient.post(`/admin/users/${userId}/reset-password`, { temporaryPassword });
};

// ============================================
// ADMIN: SUPPORT MESSAGES ENDPOINTS
// ============================================

/**
 * Get all support messages (admin only)
 */
export const getSupportMessages = async (filters = {}) => {
  return apiClient.get('/admin/support', { params: filters });
};

/**
 * Update support message status (admin only)
 */
export const updateSupportMessage = async (messageId, pk, status) => {
  return apiClient.put(`/admin/support/${encodeURIComponent(messageId)}`, { status, pk });
};

// ============================================
// ADMIN: CONTENT MANAGEMENT ENDPOINTS
// ============================================

/**
 * Create content item (admin only)
 */
export const createContent = async (data) => {
  return apiClient.post('/admin/content', data);
};

/**
 * Update content item (admin only)
 */
export const updateContent = async (pk, sk, data) => {
  return apiClient.put(`/admin/content/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}`, data);
};

/**
 * Delete content item (admin only)
 */
export const deleteContent = async (pk, sk) => {
  return apiClient.delete(`/admin/content/${encodeURIComponent(pk)}/${encodeURIComponent(sk)}`);
};

/**
 * Upload asset (admin only)
 */
export const uploadAsset = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return apiClient.post('/admin/assets/upload', formData);
};

export default apiClient;
